import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type SiteAgeGate } from "@/lib/api";

/**
 * Age-gate popup shown to new visitors on first arrival.
 *
 * Logic:
 *   1. CMS toggle (`ageGate.enabled`) — admin can switch it off completely.
 *   2. localStorage flag — once confirmed, never shown again on this device.
 *   3. Modal is gender-neutral Hebrew (noun-form, no pronouns).
 *
 * If the visitor clicks "פחות מ-18" the buttons are replaced with the deny
 * message and the page remains blocked. The visitor can still navigate
 * away via the browser back button or a fresh URL — this is a soft gate,
 * not a hard block, but it's the industry-standard pattern for alcohol
 * sites.
 */

const STORAGE_KEY = "kerem-navot:age-confirmed";

const FALLBACK_GATE: SiteAgeGate = {
  enabled: true,
  title: "ברוכים הבאים לכרם נבות",
  body: "האתר מציג ומוכר יין מכרם בוטיק. כדי להמשיך, נא לאשר כי גיל הגולש 18 ומעלה.",
  confirmLabel: "כן — להמשיך לאתר",
  denyLabel: "פחות מ-18",
  denyMessage:
    "תודה רבה על העניין. האתר אינו מאפשר המשך גלישה מתחת לגיל 18. נשמח לארח בעתיד.",
};

export function AgeGate() {
  // Three terminal states:
  //   "pending"   → still deciding whether to show (during SSR/initial render)
  //   "confirmed" → either CMS-disabled OR user already confirmed → render nothing
  //   "show"      → modal is visible
  //   "denied"    → modal is visible but shows the deny message instead of buttons
  const [state, setState] = useState<"pending" | "confirmed" | "show" | "denied">(
    "pending",
  );
  const { data } = useQuery({
    queryKey: ["site-content"],
    queryFn: api.getSiteContent,
    staleTime: 5_000,
  });
  const gate = data?.ageGate ?? FALLBACK_GATE;

  useEffect(() => {
    // Wait for the CMS response before deciding — otherwise we'd flash the
    // modal even when admin has it disabled.
    if (!data) return;
    if (!gate.enabled) {
      setState("confirmed");
      return;
    }
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") {
        setState("confirmed");
        return;
      }
    } catch {
      // Private-mode / disabled storage: show the gate, accept once per
      // pageview. Better UX than blocking forever.
    }
    setState("show");
  }, [data, gate.enabled]);

  // Lock the body scroll while the gate is visible.
  useEffect(() => {
    if (state === "show" || state === "denied") {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [state]);

  if (state !== "show" && state !== "denied") return null;

  const onConfirm = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* storage disabled — accept for this pageview only */
    }
    setState("confirmed");
  };
  const onDeny = () => {
    setState("denied");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-7 text-center">
        <h2
          id="age-gate-title"
          className="font-serif text-2xl md:text-3xl font-bold text-primary mb-4 leading-tight"
        >
          {gate.title}
        </h2>
        {state === "show" ? (
          <>
            <p className="text-base text-muted-foreground leading-relaxed mb-6 whitespace-pre-line">
              {gate.body}
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground h-12 px-6 text-base font-medium hover:bg-primary/90 transition-colors"
              >
                {gate.confirmLabel}
              </button>
              <button
                type="button"
                onClick={onDeny}
                className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
              >
                {gate.denyLabel}
              </button>
            </div>
            <p className="text-xs text-muted-foreground/80 mt-6 leading-relaxed">
              שתייה מופרזת מסוכנת לבריאות. מכירת אלכוהול מותרת מגיל 18.
            </p>
          </>
        ) : (
          <p className="text-base text-foreground leading-relaxed whitespace-pre-line">
            {gate.denyMessage}
          </p>
        )}
      </div>
    </div>
  );
}
